import { AfterViewInit, ApplicationRef, ChangeDetectorRef, Component, ElementRef, EmbeddedViewRef, OnDestroy, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from "@angular/core";
import { DxButtonComponent } from "devextreme-angular/ui/button";
import { DxChatComponent, DxChatModule } from "devextreme-angular/ui/chat";
import { AccountService, OpenAiApiKeyResponse } from "../../services/account.service";
import { Alert, Attachment, Message, MessageEnteredEvent, User } from "devextreme/ui/chat";
import { loadMessages } from "devextreme/localization";
import { CustomStore, DataSource } from "devextreme/common/data";
import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import remarkRehype from "remark-rehype";
import rehypeKatex from "rehype-katex";
import rehypeMinifyWhitespace from "rehype-minify-whitespace";
import rehypeStringify from "rehype-stringify";
import { auditTime, BehaviorSubject, catchError, debounceTime, finalize, of, Subject, takeUntil } from "rxjs";
import { Environment } from "projects/chatbot/src/environment";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { parseServerSentEvents } from "parse-sse";
import { ParsedResponseStreamEvent } from "openai/lib/responses/EventTypes.mjs";
import { ResponseOutputItem } from "openai/resources/responses/responses.mjs";
import { DxListModule, DxLoadIndicatorModule, DxPopoverModule, DxPopupModule, DxScrollViewModule } from "devextreme-angular";
import dxScrollView from "devextreme/ui/scroll_view";
import { AsyncPipe, CommonModule } from "@angular/common";
import { VoiceService } from "../../services/voice.service";
import { ItemClickEvent } from "devextreme/ui/list";
import { ClickEvent as ButtonClickEvent } from "devextreme/ui/button";
import { UploadedEvent } from "devextreme/ui/file_uploader";
import { DomSanitizer } from "@angular/platform-browser";

enum ProgressType {
    Thinking = "thinking",
    FunctionCall = "functionCall",
}

export type ChatMessage = Message & { renderedMessage?: string, completed: boolean, progressType?: ProgressType };

type ChatHistoryItemResponse = {
    id: number;
    conversationId: string;
    userMessage: string;
    aiResponse: string;
    userId: number;
    timestamp: string;
};

type ConversationSummaryResponse = {
    conversationId?: string;
    subject: string;
    timestamp: string;
};

type ConversationSummary = {
    conversationId: string;
    subject: string;
    timestamp: string;
};

type GroupedConversationSummary = {
    key: string;
    items: ConversationSummary[];
};

type MarkdownTextNode = {
    type?: string;
    value?: string;
    alt?: string;
    children?: MarkdownTextNode[];
};

const StreamTimeoutMs = 5 * 60 * 1000; // 5 minutes

@Component({
    selector: "app-home-page",
    imports: [DxButtonComponent, DxChatModule, DxLoadIndicatorModule,
        DxPopoverModule, DxPopupModule, DxListModule, AsyncPipe, CommonModule],
    templateUrl: "./home-page.component.html",
    styleUrls: ["./home-page.component.scss"],
})
export class HomePageComponent implements OnInit, AfterViewInit, OnDestroy {
    public static readonly Path = "";
    public readonly ProgressType = ProgressType;
    @ViewChild(DxChatComponent) public chatComponent!: DxChatComponent;
    @ViewChild("extraButtons") public extraButtonsTemplate?: TemplateRef<unknown>;

    public assistant: User = {
        id: "assistant",
        name: "AI assistant",
    };

    public store: ChatMessage[] = [];
    public alerts: Alert[] = [];
    public output: ResponseOutputItem[] = [];
    public conversationSummaries: ConversationSummary[] = [];
    public groupedConversationSummaries: GroupedConversationSummary[] = [];
    public isConversationPopoverVisible = false;
    public isApiKeyPopoverVisible = false;
    public isDeleteConversationPopupVisible = false;
    public isVoiceModeEnabled = false;
    public isDisabled = false;
    public isOpenAiApiKeyLoading = false;
    public isOpenAiApiKeyCopied = false;
    public isReadingAloud = false;
    public apiKey?: string;
    public openAiApiKeyBaseUrl?: string;
    public anthropicApiKeyBaseUrl?: string;
    public openAiApiKeyError?: string;
    public readonly fileUploadUrl = `${Environment.ApiUri}/ai/upload-file`;
    public fileUploadHeaders: Record<string, string> = {};
    public readonly handleFileUploaded = (event: UploadedEvent): void => {
        const response = JSON.parse(event.request?.responseText ?? "{}") as { fileId?: string };
        if (response.fileId && event.file?.name) {
            this.uploadedFileIds.set(event.file.name, response.fileId);
        }
    };
    private uploadedFileIds = new Map<string, string>(); // filename -> fileId from server
    private lastScrollOffset?: number;
    private triggerScrollLastUserMessageToTop = new Subject<boolean>();
    private destroyed = new Subject<void>();
    private interruptController$ = new Subject<void>();
    private extraButtonsViewRef?: EmbeddedViewRef<unknown>;
    private injectExtraButtonsTimeoutId?: ReturnType<typeof setTimeout>;
    private toolbarMutationObserver?: MutationObserver;
    private isInjectingExtraButtons = false;
    private recordedText = "";
    private debounceVoiceMessage$ = new Subject<void>();

    public dataSource = new DataSource({
        store: new CustomStore<ChatMessage, string>({
            key: "id",
            load: () => new Promise((resolve) => {
                setTimeout(() => resolve([...this.store]));
            }),
            insert: (message) => new Promise((resolve) => {
                setTimeout(() => {
                    this.store.push(message);
                    resolve(message);
                });
            }),
        }),
        paginate: false,
    });

    private alertsSubject = new BehaviorSubject<Alert[]>([]);
    private markdownParser = unified()
        .use(remarkParse)
        .use(remarkGfm)
        .use(remarkMath, { singleDollarTextMath: true })
        .use(remarkRehype)
        .use(rehypeKatex)
        .use(rehypeMinifyWhitespace)
        .use(rehypeStringify);
    private markdownTextParser = unified()
        .use(remarkParse)
        .use(remarkGfm)
        .use(remarkMath, { singleDollarTextMath: true });

    public conversationId?: string;
    private previousResponseId?: string;
    private oldPreviousResponseId?: string;
    private pendingConversationToDelete?: string;

    /**
     * Polyfill for `ReadableStream[Symbol.asyncIterator]` not being supported in Safari.
     *
     * Ideally would just do the following:
     *   `for await (const streamEvent of parseServerSentEvents(response))`
     */
    private async* iterReadableStream<T>(stream: ReadableStream<T>) {
        const reader = stream.getReader();
        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    return;
                }

                yield value;
            }
        } finally {
            reader.releaseLock();
        }
    }

    public constructor(
        private accountService: AccountService,
        private elementRef: ElementRef,
        private changeDetectorRef: ChangeDetectorRef,
        private appRef: ApplicationRef,
        private voiceService: VoiceService,
        private httpClient: HttpClient,
        private sanitize: DomSanitizer,
    ) {
        this.updateFileUploadHeaders();

        loadMessages({
            en: {
                // "dxChat-emptyListMessage": "Chat is Empty",
                "dxChat-emptyListPrompt": "The AI assistant is ready to answer your questions.",
                "dxChat-textareaPlaceholder": "Ask AI assistant...",
            },
        });

        this.triggerScrollLastUserMessageToTop.pipe(
            auditTime(500),
            takeUntil(this.destroyed),
            finalize(() => this.destroyed.complete()),
        ).subscribe((shouldScroll) => {
            if (shouldScroll) {
                this.scrollLastUserMessageViewToTop();
            }
        });

        this.debounceVoiceMessage$.pipe(
            debounceTime(3000),
            takeUntil(this.destroyed),
        ).subscribe(() => this.sendVoiceMessage());
    }

    public ngOnInit(): void {
        this.loadConversation();
        this.loadConversationSummaries();
    }

    public ngAfterViewInit(): void {
        this.injectExtraButtonsToToolbar();
    }

    public ngOnDestroy(): void {
        this.voiceService.interruptRead();
        clearTimeout(this.injectExtraButtonsTimeoutId);
        this.toolbarMutationObserver?.disconnect();

        if (this.extraButtonsViewRef) {
            this.appRef.detachView(this.extraButtonsViewRef);
            this.extraButtonsViewRef.destroy();
            this.extraButtonsViewRef = undefined;
        }

        this.triggerScrollLastUserMessageToTop.complete();
        this.destroyed.next();
        this.interruptController$.complete();
    }

    public get alerts$() {
        return this.alertsSubject.asObservable();
    }

    public get loggedInEmail() {
        return this.accountService.authEmail;
    }

    public get chatUser() {
        return {
            id: "current-user",
            name: this.loggedInEmail || "User",
        };
    }

    public get voiceModeIcon() {
        return this.isVoiceModeEnabled ? "fa-microphone" : "fa-microphone-slash";
    }

    public switchConversation(e: ItemClickEvent) {
        const conversationId = e.itemData.conversationId;
        if (conversationId === this.conversationId) {
            return;
        }

        window.console.log("DEBUG: Switching conversation to ID " + conversationId);
        this.loadConversation(conversationId);
        this.isConversationPopoverVisible = false;
    }

    public deleteConversation(conversationId: string, event?: ButtonClickEvent) {
        event?.event?.preventDefault();
        event?.event?.stopPropagation();

        if (!conversationId) {
            return;
        }

        this.pendingConversationToDelete = conversationId;
        this.isDeleteConversationPopupVisible = true;
    }

    public confirmDeleteConversation() {
        const conversationId = this.pendingConversationToDelete;
        this.pendingConversationToDelete = undefined;
        this.isDeleteConversationPopupVisible = false;

        if (!conversationId) {
            return;
        }

        const authToken = this.accountService.authToken;
        if (!authToken) {
            return;
        }

        this.httpClient.delete(`${Environment.ApiUri}/ai/delete-conversation?conversationId=${encodeURIComponent(conversationId)}`, {
            headers: {
                "Authorization": `Bearer ${authToken}`,
            },
            responseType: "text",
        }).pipe(
            catchError((error: any) => {
                window.console.error("Failed to delete conversation", error);
                this.alertError((error.data?.message ?? error.error?.message ?? error.message ?? error?.toString()) || "Unable to delete the conversation.");
                return of(null);
            }),
        ).subscribe((response) => {
            if (response === null) {
                return;
            }

            this.conversationSummaries = this.conversationSummaries.filter((summary) => summary.conversationId !== conversationId);
            this.groupedConversationSummaries = this.groupConversationSummariesByDay(this.conversationSummaries);

            if (this.conversationId === conversationId) {
                this.loadConversation();
            }

            this.changeDetectorRef.detectChanges();
        });
    }

    public cancelDeleteConversation() {
        this.pendingConversationToDelete = undefined;
        this.isDeleteConversationPopupVisible = false;
    }

    public logout() {
        this.accountService.logout();
    }

    public async onMessageEntered(event: MessageEnteredEvent) {
        await this.sendUserMessage(event);
    }

    private async sendUserMessage(messageEnteredEvent: MessageEnteredEvent) {
        if (this.isDisabled) {
            return;
        }

        const normalizedText = messageEnteredEvent.message.text?.trim() ?? "";
        const normalizedMessage = normalizedText === (messageEnteredEvent.message.text ?? "")
            ? messageEnteredEvent.message
            : { ...messageEnteredEvent.message, text: normalizedText };

        if (!normalizedText && !(normalizedMessage.attachments?.length)) {
            return;
        }

        const normalizedEvent = normalizedMessage === messageEnteredEvent.message
            ? messageEnteredEvent
            : { ...messageEnteredEvent, message: normalizedMessage } as MessageEnteredEvent;

        if (this.isVoiceModeEnabled) {
            this.voiceService.stopRecording();
        }

        this.alerts.splice(0, this.alerts.length);
        this.alertsSubject.next(this.alerts);
        this.toggleDisabledState(true);
        this.setNextUserChangeMessage("");

        // add users message
        this.updateStore("insert", { id: Date.now().toString(), ...normalizedEvent.message, completed: true });

        await this.processMessageSending(normalizedEvent);
    }

    public cancelResponse() {
        this.interruptController$.next();
    }

    public showConversationPopover() {
        if (this.isDisabled || this.isConversationPopoverVisible) {
            return;
        }

        this.isConversationPopoverVisible = true;
    }

    public hideConversationPopover() {
        this.isConversationPopoverVisible = false;
    }

    public showApiKeyPopover() {
        if (this.isDisabled || this.isApiKeyPopoverVisible) {
            return;
        }

        this.isApiKeyPopoverVisible = true;
        this.isOpenAiApiKeyCopied = false;
        this.loadOpenAiApiKey();
    }

    public hideApiKeyPopover() {
        this.isApiKeyPopoverVisible = false;
        this.isOpenAiApiKeyCopied = false;
    }

    public async copyOpenAiApiKey() {
        if (!this.apiKey) {
            return;
        }

        try {
            await this.writeToClipboard(this.apiKey);
            this.isOpenAiApiKeyCopied = true;
            this.openAiApiKeyError = undefined;
            this.changeDetectorRef.detectChanges();
            setTimeout(() => {
                this.isOpenAiApiKeyCopied = false;
                this.changeDetectorRef.detectChanges();
            }, 3000);
        } catch (error) {
            window.console.error("Failed to copy OpenAI API key", error);
            this.openAiApiKeyError = "Unable to copy the API key to the clipboard.";
        }
    }

    public toggleVoiceMode() {
        this.isVoiceModeEnabled = !this.isVoiceModeEnabled;
        if (this.isVoiceModeEnabled) {
            this.startRecording();
        } else {
            this.voiceService.interruptRead();
            this.voiceService.stopRecording();
            this.recordedText = "";
            this.setNextUserChangeMessage("");
            // void this.voiceService.readText(this.recordedText);
        }
    }

    public async readText(text: string) {
        if (text) {
            this.disableVoiceMode();
            this.voiceService.interruptRead();
            this.isReadingAloud = true;
            await this.voiceService.readText(this.convertMarkdownToPlainText(text));
            this.isReadingAloud = false;
            this.changeDetectorRef.detectChanges();
        }
    }

    private disableVoiceMode() {
        if (this.isVoiceModeEnabled) {
            this.isVoiceModeEnabled = false;
            this.voiceService.interruptRead();
            this.voiceService.stopRecording();
            this.recordedText = "";
            this.setNextUserChangeMessage("");
        }
    }

    public interruptRead() {
        this.voiceService.interruptRead();
        this.isReadingAloud = false;
    }

    private startRecording() {
        this.recordedText = "";
        this.setNextUserChangeMessage("");
        this.voiceService.startRecording().pipe(
            takeUntil(this.destroyed),
        ).subscribe((text) => {
            this.recordedText = text;
            this.setNextUserChangeMessage(this.recordedText);
            window.console.info("Voice input:", text);
            this.debounceVoiceMessage$.next();
        });
    }

    private sendVoiceMessage() {
        if (this.recordedText.trim().length > 0) {
            this.voiceService.stopRecording();
            this.setNextUserChangeMessage(this.recordedText);
            void this.sendRecordedMessage();
            this.recordedText = "";
        }
    }

    private async sendRecordedMessage() {
        const messageText = this.recordedText.trim();
        if (!messageText || this.isDisabled) {
            return;
        }

        this.setNextUserChangeMessage(messageText);
        await this.sendUserMessage(this.createMessageEnteredEvent(messageText));
    }

    private createMessageEnteredEvent(text: string): MessageEnteredEvent {
        return {
            event: new Event("submit"),
            message: {
                author: this.chatUser,
                text,
                timestamp: new Date(),
                type: "text",
            },
        } as MessageEnteredEvent;
    }

    private setNextUserChangeMessage(messageText: string): void {
        const chatInstance = this.chatComponent?.instance as unknown as {
            _messageBox?: { option: (name: string, value: string) => void };
        } | undefined;

        if (chatInstance?._messageBox) {
            chatInstance._messageBox.option("text", messageText);
            return;
        }

        const textArea = this.elementRef.nativeElement
            .querySelector("textarea.dx-texteditor-input[placeholder='Ask AI assistant...']") as HTMLTextAreaElement | null;

        if (!textArea || textArea.value === messageText) {
            return;
        }

        textArea.value = messageText;
        textArea.dispatchEvent(new Event("input", { bubbles: true }));
    }

    private updateFileUploadHeaders(): void {
        const authToken = this.accountService.authToken;
        this.fileUploadHeaders = authToken ? { Authorization: `Bearer ${authToken}` } : {};
    }

    private loadOpenAiApiKey() {
        const authToken = this.accountService.authToken;
        if (!authToken) {
            this.apiKey = undefined;
            this.openAiApiKeyBaseUrl = undefined;
            this.anthropicApiKeyBaseUrl = undefined;
            this.openAiApiKeyError = "You are not logged in.";
            return;
        }

        this.isOpenAiApiKeyLoading = true;
        this.apiKey = undefined;
        this.openAiApiKeyBaseUrl = undefined;
        this.anthropicApiKeyBaseUrl = undefined;
        this.openAiApiKeyError = undefined;

        this.accountService.getOpenAiApiKey().pipe(
            catchError((error: any) => {
                window.console.error("Failed to load OpenAI API key", error);
                this.openAiApiKeyError = (error.error?.message ?? error.message ?? error?.toString()) || "Unable to load the API key.";
                return of(undefined);
            }),
            finalize(() => {
                this.isOpenAiApiKeyLoading = false;
                setTimeout(() => this.changeDetectorRef.detectChanges(), 500);
            }),
        ).subscribe((response) => {
            if (!response) {
                return;
            }

            const keyResponse = response as OpenAiApiKeyResponse;
            this.apiKey = keyResponse.apiKey;
            this.openAiApiKeyBaseUrl = keyResponse.openaiBaseUrl;
            this.anthropicApiKeyBaseUrl = keyResponse.anthropicBaseUrl;
            this.openAiApiKeyError = undefined;
        });
    }

    private async writeToClipboard(value: string) {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(value);
            return;
        }

        const textArea = document.createElement("textarea");
        textArea.value = value;
        textArea.setAttribute("readonly", "true");
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();

        try {
            const didCopy = document.execCommand("copy");
            if (!didCopy) {
                throw new Error("Clipboard copy command was rejected.");
            }
        } finally {
            document.body.removeChild(textArea);
        }
    }

    private toggleDisabledState(disabled: boolean) {
        this.isDisabled = disabled;
        setTimeout(() => this.changeDetectorRef.detectChanges(), 500);

        const target = event?.target;
        if (target instanceof HTMLElement) {
            if (disabled) {
                target.blur();
            } else {
                target.focus();
            }
        }

        if (!disabled) { // The above focus not bring back the cursor into the text area even with timeout - so just try this direct capture into the text area
            setTimeout(() => {
                const textArea = document.querySelector("textarea.dx-texteditor-input[placeholder='Ask AI assistant...']") as HTMLElement;
                textArea?.focus();
            }, 100);
        }
    }

    private async processMessageSending(messageEnteredEvent: MessageEnteredEvent) {
        let message: ChatMessage | undefined = undefined;
        let progressMessage: ChatMessage | undefined =
            this.insertAssistantMessage("Request Submitted...", ProgressType.FunctionCall, "Waiting for AI response");

        // Collect IDs for any files that were uploaded alongside this message
        const attachments: Attachment[] = messageEnteredEvent.message.attachments ?? [];
        const attachmentIds = attachments
            .map((a: Attachment) => this.uploadedFileIds.get(a.name))
            .filter((id): id is string => !!id);
        for (const a of attachments) {
            this.uploadedFileIds.delete(a.name);
        }

        const body = {
            email: this.loggedInEmail,
            userChatMessage: messageEnteredEvent.message.text,
            conversationId: this.conversationId,
            previousResponseId: this.previousResponseId,
            attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
            isSpeechMode: this.isVoiceModeEnabled,
        };

        const controller = new AbortController();
        const interruptSubscription = this.interruptController$.pipe(
            takeUntil(this.destroyed),
        ).subscribe(() => {
            controller.abort("interrupted");
        });
        let streamTimeoutId: ReturnType<typeof setTimeout> | undefined;

        try {
            const response = await fetch(`${Environment.ApiUri}/ai/responses`, {
                method: "POST",
                body: JSON.stringify(body),
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${this.accountService.authToken!}`,
                },
                signal: controller.signal,
            });

            if (response.status < 200 || response.status >= 300) {
                throw new HttpErrorResponse({
                    status: response.status,
                    statusText: response.statusText,
                    url: response.url,
                });
            }

            // Start the stream timeout - if the stream takes longer than 5 minutes, abort the connection
            streamTimeoutId = setTimeout(() => controller.abort(), StreamTimeoutMs);

            // this should just be the following:
            //  for await (const streamEvent of parseServerSentEvents(response))
            // however, this is not supported in Safari. have to manually read the reader.
            for await (const streamEvent of this.iterReadableStream(parseServerSentEvents(response))) {
                if (streamEvent.data === "[DONE]") {
                    break;
                }

                // Handle custom conversation.created event from the server (sent on first message only)
                if (streamEvent.type === "conversation.created") {
                    const conversationData = JSON.parse(streamEvent.data) as { conversationId: string };
                    this.conversationId = conversationData.conversationId;
                    continue;
                }

                const event = JSON.parse(streamEvent.data) as ParsedResponseStreamEvent;
                if (event.type === "error") {
                    throw event;
                }

                if (event.type === "response.created") {
                    this.oldPreviousResponseId = this.previousResponseId;
                    this.previousResponseId = event.response.id;
                    this.output = [];
                }

                // window.console.info("DEBUG: Stream event type: " + event.type + " name: " + (event as any).name);
                if ((event.type as any) === "function_call") {
                    const renderedFunctionCallArgs = this.formatFunctionCallArgs((event as any).args);
                    if (!progressMessage) {
                        progressMessage = this.insertAssistantMessage(`${HomePageComponent.camelToSentenceCase((event as any).name)}...`, ProgressType.FunctionCall, renderedFunctionCallArgs);
                    } else {
                        progressMessage.text = `${HomePageComponent.camelToSentenceCase((event as any).name)}...`;
                        progressMessage.renderedMessage = renderedFunctionCallArgs;
                        this.updateAssistantMessage(progressMessage);
                    }

                    setTimeout(() => this.scrollToBottom(), 100);
                }

                if (event.type === "response.output_item.added") {
                    this.output.splice(event.output_index, 0, event.item);

                    // if (progressMessage) {
                    //   if (event.item.type === "function_call") {
                    //     progressMessage.progressType = ProgressType.FunctionCall;
                    //     progressMessage.text = "Function call <TODO>...";
                    //     this.updateAssistantMessage(progressMessage);
                    //   } else if (event.item.type !== "message") {
                    //     progressMessage.progressType = ProgressType.Thinking;
                    //     progressMessage.text = "Thinking...";
                    //     this.updateAssistantMessage(progressMessage);
                    //   } else if (event.item.type === "message") {
                    //     // started outputting message, we can remove the progress message now...
                    //     this.removeAssistantMessage(progressMessage);
                    //   }
                    // }
                }

                // if (progressMessage && event.type === "response.function_call_arguments.done" && event.arguments) {
                //   const functionArgs = JSON.parse(event.arguments);
                //   if (functionArgs.entityType && typeof functionArgs.entityType === "string") {
                //     // const entityTypeWithoutNamespace = (functionArgs.entityType as string).split(".").pop();
                //     // const withSpaces = StringUtilities.addSpacesToPascalCase(entityTypeWithoutNamespace ?? "");
                //     // if (withSpaces) {
                //     //   progressMessage.text = "Retrieving " + withSpaces.toLowerCase() + "...";
                //     //   this.updateAssistantMessage(chat, progressMessage);
                //     // }
                //   }
                // }

                if (event.type === "response.output_item.done") {
                    Object.assign(this.output[event.output_index], event.item);
                }

                if (event.type === "response.content_part.added" || event.type === "response.content_part.done") {
                    const messageResponse = this.output[event.output_index];
                    if (messageResponse.type === "message" && event.part.type !== "reasoning_text") {
                        // add content to recorded output so we can update it in delta
                        messageResponse.content.splice(event.content_index, 0, event.part);

                        if (this.previousResponseId) {
                            const text = event.part.type === "output_text"
                                ? event.part.text
                                : event.part.refusal;
                            if (text) {
                                if (!message) {
                                    message = this.insertAssistantMessage(text);
                                } else {
                                    message.text = text;
                                }
                                this.updateAssistantMessage(message);
                            }
                        }
                    }
                }

                if (event.type === "response.output_text.delta" || event.type === "response.output_text.done") {
                    if (progressMessage) {
                        this.removeAssistantMessage(progressMessage);
                        progressMessage = undefined;
                    }

                    if (!message) {
                        message = this.insertAssistantMessage("");
                    }

                    const responseOutput = this.output[event.output_index];
                    if (responseOutput.type === "message" && message) {
                        const content = responseOutput.content[event.content_index];
                        if (content.type === "output_text") {
                            if (event.type === "response.output_text.delta") {
                                message.text += event.delta;
                            } else {
                                message.text = event.text;
                            }

                            this.updateAssistantMessage(message);
                        }
                    }

                    if (event.type === "response.output_text.delta" && message) {
                        this.triggerScrollLastUserMessageToTop.next(true);
                    } else if (message) {
                        this.triggerScrollLastUserMessageToTop.next(false);
                        this.lastScrollOffset = undefined;
                    }
                }

                if (event.type === "response.incomplete" && event.response.incomplete_details) {
                    const errorMessage = event.response.incomplete_details.reason === "max_output_tokens"
                        ? "You have reached the context limit. Please start a new chat."
                        : "Your message triggered the AI's content filter. The response has been cancelled.";
                    this.alertError(errorMessage);

                    if (message) {
                        this.completeChatMessage(message);
                    }
                }

                if (event.type === "response.completed") {
                    if (message) {
                        this.completeChatMessage(message);
                    }
                }
            }
        } catch (error: any) {
            this.previousResponseId = this.oldPreviousResponseId;
            if (controller.signal.aborted) {
                if (controller.signal.reason === "interrupted") {
                    window.console.warn("Chat stream interrupted by user", error);
                    this.alertError("The AI response was cancelled.");
                    if (message) {
                        message.text += "\n\n---\n*(response cancelled)*";
                        this.updateAssistantMessage(message);
                    }
                } else {
                    const timeoutError = new HttpErrorResponse({
                        status: 408,
                        statusText: "Request Timeout",
                        url: `${Environment.ApiUri}/ai/responses`,
                    });
                    window.console.error("Chat stream timed out after 5 minutes", timeoutError);
                    this.alertError("The AI response timed out after 5 minutes.");
                }
            } else {
                window.console.error("Chat error", error);
                this.alertError((error.data?.message ?? error.error?.message ?? error.message ?? error?.toString()) || "An error occurred while processing the chat.");
            }
        } finally {
            clearTimeout(streamTimeoutId);
            interruptSubscription.unsubscribe();

            if (message && !message.completed) {
                this.completeChatMessage(message);
            }

            if (progressMessage) {
                this.removeAssistantMessage(progressMessage);
                progressMessage = undefined;
            }

            this.toggleDisabledState(false);
        }
    }

    private loadConversation(conversationId?: string | number) {
        const authToken = this.accountService.authToken;
        if (!authToken) {
            return;
        }

        const requestedConversationId = conversationId === undefined ? undefined : String(conversationId);
        const endpoint = requestedConversationId
            ? `${Environment.ApiUri}/ai/get-conversation?conversationId=${encodeURIComponent(requestedConversationId)}`
            : `${Environment.ApiUri}/ai/last-conversation`;

        this.httpClient.get<ChatHistoryItemResponse[]>(endpoint, {
            headers: {
                "Authorization": `Bearer ${authToken}`,
            },
        }).pipe(
            catchError((error: any) => {
                window.console.error("Failed to load conversation", error);
                this.alertError((error.data?.message ?? error.error?.message ?? error.message ?? error?.toString()) || "Unable to load the conversation.");
                return of([] as ChatHistoryItemResponse[]);
            }),
        ).subscribe((historyItems) => {
            this.conversationId = requestedConversationId ?? historyItems[0]?.conversationId;
            this.previousResponseId = undefined;
            this.oldPreviousResponseId = undefined;

            this.store.splice(0, this.store.length);
            for (const item of Array.isArray(historyItems) ? historyItems : []) {
                const timestamp = new Date(item.timestamp);

                if (item.userMessage) {
                    this.store.push({
                        id: `history-${item.id}-user`,
                        type: "text",
                        timestamp,
                        author: this.chatUser,
                        text: item.userMessage,
                        completed: true,
                    });
                }

                if (item.aiResponse) {
                    this.store.push({
                        id: `history-${item.id}-assistant`,
                        type: "text",
                        timestamp: new Date(timestamp.getTime() + 1),
                        author: this.assistant,
                        text: item.aiResponse,
                        completed: true,
                    });
                }
            }

            this.dataSource.load();
            this.repaintChat();
            this.changeDetectorRef.detectChanges();
        });
    }

    private loadConversationSummaries() {
        const authToken = this.accountService.authToken;
        if (!authToken) {
            return;
        }

        this.httpClient.get<ConversationSummaryResponse[]>(`${Environment.ApiUri}/ai/conversation-summaries`, {
            headers: {
                "Authorization": `Bearer ${authToken}`,
            },
        }).pipe(
            catchError((error: any) => {
                window.console.error("Failed to load conversation summaries", error);
                this.alertError((error.data?.message ?? error.error?.message ?? error.message ?? error?.toString()) || "Unable to load previous conversations.");
                return of([] as ConversationSummaryResponse[]);
            }),
        ).subscribe((summaries) => {
            this.conversationSummaries = (Array.isArray(summaries) ? summaries : [])
                .map((summary) => ({
                    conversationId: summary.conversationId ?? "",
                    subject: summary.subject,
                    timestamp: summary.timestamp,
                }))
                .filter((summary) => !!summary.conversationId)
                .sort((left, right) => new Date(right.timestamp).getTime() - new Date(left.timestamp).getTime());
            this.groupedConversationSummaries = this.groupConversationSummariesByDay(this.conversationSummaries);

            this.changeDetectorRef.detectChanges();
        });
    }

    private groupConversationSummariesByDay(summaries: ConversationSummary[]): GroupedConversationSummary[] {
        const groups: GroupedConversationSummary[] = [];

        for (const summary of summaries) {
            const groupKey = this.getConversationSummaryDayLabel(summary.timestamp);
            const currentGroup = groups.at(-1);

            if (!currentGroup || currentGroup.key !== groupKey) {
                groups.push({
                    key: groupKey,
                    items: [summary],
                });
                continue;
            }

            currentGroup.items.push(summary);
        }

        return groups;
    }

    private static camelToSentenceCase(str: string) {
        return str.replace(/([A-Z])/g, (match) => ` ${match.toLowerCase()}`)
            .trim()
            .replace(/^./, (match) => match.toUpperCase())
            .trim();
    }

    private getConversationSummaryDayLabel(timestamp: string): string {
        const summaryDate = new Date(timestamp);
        if (Number.isNaN(summaryDate.getTime())) {
            return "Unknown date";
        }

        const today = new Date();
        const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const startOfSummaryDay = new Date(summaryDate.getFullYear(), summaryDate.getMonth(), summaryDate.getDate());
        const dayDifference = Math.round((startOfToday.getTime() - startOfSummaryDay.getTime()) / (24 * 60 * 60 * 1000));

        if (dayDifference === 0) {
            return "Today";
        }

        if (dayDifference === 1) {
            return "Yesterday";
        }

        return summaryDate.toLocaleDateString(undefined, {
            weekday: "long",
            month: "short",
            day: "numeric",
            year: startOfToday.getFullYear() === startOfSummaryDay.getFullYear() ? undefined : "numeric",
        });
    }

    public formatConversationSummaryTime(timestamp: string): string {
        const summaryDate = new Date(timestamp);
        if (Number.isNaN(summaryDate.getTime())) {
            return "Unknown time";
        }

        return summaryDate.toLocaleTimeString(undefined, {
            hour: "numeric",
            minute: "2-digit",
        });
    }

    private normalizeMarkdown(rawMessage: string): string {
        return rawMessage
            .replace(/^```markdown\r?\n/, "")
            .replace(/\r?\n```$/, "")
            .replace(/\\\[\s*([\s\S]*?)\s*\\\]/g, (_, expression: string) => `$$\n${expression.trim()}\n$$`)
            .replace(/\\\((.+?)\\\)/g, (_, expression: string) => `$${expression.trim()}$`);
    }

    private formatFunctionCallArgs(rawArgs: unknown): string | undefined {
        if (typeof rawArgs !== "string" || !rawArgs.trim()) {
            return undefined;
        }

        let displayValue = rawArgs.trim();

        try {
            const parsedArgs = JSON.parse(displayValue) as { query?: unknown, url?: unknown, location?: unknown };
            if (typeof parsedArgs.query === "string" && parsedArgs.query.trim()) {
                displayValue = parsedArgs.query.trim();
            } else if (typeof parsedArgs.url === "string" && parsedArgs.url.trim()) {
                displayValue = parsedArgs.url.trim();
            } else if (typeof parsedArgs.location === "string" && parsedArgs.location.trim()) {
                displayValue = parsedArgs.location.trim();
            }
        } catch {
            // Keep the original value when the arguments are not valid JSON.
        }

        if (displayValue.trim() === "{}" || !displayValue.trim()) {
            displayValue = "Thinking...";
        }

        if (displayValue.length > 60) {
            displayValue = `${displayValue.slice(0, 60)}...`;
        }

        return this.sanitize.bypassSecurityTrustHtml(this.markdownParser.processSync(this.normalizeMarkdown(displayValue)).toString()) as string;
    }

    public convertToHtml(message: ChatMessage): string {
        const rawMessage = message.text;
        if (rawMessage) {
            const cleaned = this.normalizeMarkdown(rawMessage);
            const renderedMessage = this.sanitize.bypassSecurityTrustHtml(this.markdownParser.processSync(cleaned).toString()) as string;
            message.renderedMessage = renderedMessage;
            return renderedMessage;
        } else {
            return rawMessage;
        }
    }

    private convertMarkdownToPlainText(rawMessage: string): string {
        const markdownTree = this.markdownTextParser.parse(this.normalizeMarkdown(rawMessage)) as MarkdownTextNode;

        return this.normalizePlainText(this.extractMarkdownNodeText(markdownTree));
    }

    private extractMarkdownNodeText(node?: MarkdownTextNode): string {
        if (!node) {
            return "";
        }

        switch (node.type) {
            case "text":
            case "inlineCode":
            case "code":
            case "inlineMath":
            case "math":
                return node.value ?? "";
            case "image":
                return node.alt ?? "";
            case "break":
            case "thematicBreak":
                return "\n";
        }

        const children = node.children ?? [];
        const separator = this.getMarkdownNodeTextSeparator(node.type);

        return children
            .map((child) => this.extractMarkdownNodeText(child))
            .filter((text) => !!text)
            .join(separator);
    }

    private getMarkdownNodeTextSeparator(nodeType?: string): string {
        switch (nodeType) {
            case "root":
            case "paragraph":
            case "heading":
            case "blockquote":
            case "list":
            case "listItem":
            case "table":
            case "tableRow":
                return "\n";
            case "tableCell":
                return " ";
            default:
                return "";
        }
    }

    private normalizePlainText(text: string): string {
        return text
            .replace(/[ \t]+\n/g, "\n")
            .replace(/\n[ \t]+/g, "\n")
            .replace(/[ \t]{2,}/g, " ")
            .replace(/\n{3,}/g, "\n\n")
            .trim();
    }

    public startNewChat() {
        this.hideConversationPopover();

        this.httpClient.get(`${Environment.ApiUri}/ai/clear-conversation`, {
            headers: {
                "Authorization": `Bearer ${this.accountService.authToken!}`,
            },
            responseType: "text",
        }).pipe(
            catchError((error: any) => {
                window.console.error("Failed to clear conversation", error);
                this.alertError((error.data?.message ?? error.error?.message ?? error.message ?? error?.toString()) || "An error occurred while starting a new chat.");
                return of(null);
            }),
        ).subscribe((response) => {
            if (response === null) {
                return;
            }

            this.conversationId = undefined;
            this.previousResponseId = undefined;
            this.oldPreviousResponseId = undefined;
            this.alerts.splice(0, this.alerts.length);
            this.alertsSubject.next(this.alerts);
            this.store.splice(0, this.store.length);
            this.dataSource.load();
        });
    }

    private alertError(message: string) {
        const alert: Alert = { message: `${message} - Try starting a new chat to continue.` };
        this.alerts.push(alert);
        this.alertsSubject.next(this.alerts);
    }

    private updateStore(type: "insert" | "update" | "remove", message: ChatMessage) {
        this.dataSource.store().push([{ type, data: message, key: message.id?.toString() }]);
    }

    private insertAssistantMessage(text: string, progressType?: ProgressType, renderedMessage?: string) {
        const message: ChatMessage = {
            id: this.previousResponseId! + (progressType ? "_progress" : ""),
            type: "text",
            timestamp: new Date(),
            author: this.assistant,
            completed: false,
            text,
            progressType,
            renderedMessage,
        };

        this.updateStore("insert", message);

        return message;
    }

    private updateAssistantMessage(message: ChatMessage) {
        if (message.progressType !== ProgressType.FunctionCall) {
            this.convertToHtml(message);
        }

        this.updateStore("update", message);
    }

    private removeAssistantMessage(message: ChatMessage) {
        this.updateStore("remove", message);
    }

    private completeChatMessage(message: ChatMessage) {
        message.completed = true;
        message.text += " "; // need this to trigger chat rebind
        this.updateAssistantMessage(message);
        setTimeout(() => {
            this.triggerScrollLastUserMessageToTop.next(true);
            this.lastScrollOffset = undefined;
            this.toggleDisabledState(false);
        }, 500);

        if (this.conversationId) {
            let summary = this.conversationSummaries.find((summary) => summary.conversationId === this.conversationId);
            if (!summary) {
                summary = {
                    conversationId: this.conversationId,
                    subject: message.text,
                    timestamp: new Date().toISOString(),
                };
                this.conversationSummaries.unshift(summary);
            }

            summary.subject = message.text.length > 60 ? message.text.substring(0, 60) + "..." : message.text;
            summary.timestamp = new Date().toISOString();
            this.conversationSummaries = this.conversationSummaries.sort((left, right) => new Date(right.timestamp).getTime() - new Date(left.timestamp).getTime());
            this.groupedConversationSummaries = this.groupConversationSummariesByDay(this.conversationSummaries);
        }

        if (this.isVoiceModeEnabled) {
            void this.restartRecordingAfterPlayback(message.text);
        }

        this.changeDetectorRef.detectChanges();
    }

    private async restartRecordingAfterPlayback(text: string): Promise<void> {
        this.voiceService.interruptRead();
        this.isReadingAloud = true;
        await this.voiceService.readText(this.convertMarkdownToPlainText(text));
        this.isReadingAloud = false;
        this.changeDetectorRef.detectChanges();

        if (!this.isVoiceModeEnabled || this.isDisabled) {
            return;
        }

        this.startRecording();
    }

    private repaintChat() {
        setTimeout(() => this.chatComponent?.instance.repaint(), 100);
    }

    private injectExtraButtonsToToolbar(attempt = 0): void {
        if (this.isInjectingExtraButtons) {
            return;
        }

        const chatRoot = this.chatComponent?.instance?.element() as HTMLElement | undefined;
        const toolbarContainer = (chatRoot ?? this.elementRef.nativeElement)
            .querySelector(".dx-toolbar-items-container > .dx-toolbar-before") as HTMLElement | null;

        if (!toolbarContainer || !this.extraButtonsTemplate) {
            if (attempt < 10) {
                this.injectExtraButtonsTimeoutId = setTimeout(() => this.injectExtraButtonsToToolbar(attempt + 1), 200);
            }

            return;
        }

        if (!this.toolbarMutationObserver) {
            const toolbarItemsContainer = toolbarContainer.parentElement;
            if (toolbarItemsContainer) {
                this.toolbarMutationObserver = new MutationObserver(() => {
                    this.injectExtraButtonsToToolbar();
                });

                this.toolbarMutationObserver.observe(toolbarItemsContainer, {
                    childList: true,
                    subtree: true,
                });
            }
        }

        if (!this.extraButtonsViewRef) {
            this.extraButtonsViewRef = this.extraButtonsTemplate.createEmbeddedView({});
            this.appRef.attachView(this.extraButtonsViewRef);
            this.extraButtonsViewRef.detectChanges();
        }

        const buttonNodes = this.extraButtonsViewRef.rootNodes.filter((node) => node.nodeType === Node.ELEMENT_NODE);
        if (buttonNodes.length === 0) {
            return;
        }

        this.isInjectingExtraButtons = true;
        // Insert from back to front so node order remains stable when prepending.
        for (let index = buttonNodes.length - 1; index >= 0; index -= 1) {
            const buttonNode = buttonNodes[index] as HTMLElement;
            if (!toolbarContainer.contains(buttonNode)) {
                toolbarContainer.insertBefore(buttonNode, toolbarContainer.firstChild);
            }
        }
        this.isInjectingExtraButtons = false;
    }

    private scrollLastUserMessageViewToTop() {
        const lastMessageGroup = this.elementRef?.nativeElement.querySelector(".dx-chat-messagegroup.dx-chat-last-messagegroup-alignment-end");
        if (lastMessageGroup) {
            const el = this.elementRef?.nativeElement.querySelector(".dx-chat .dx-scrollable");
            const scrollView = dxScrollView.getInstance(el) as dxScrollView;
            if (scrollView) {
                const offset = (lastMessageGroup as HTMLElement).offsetTop;
                const currentOffset = scrollView.scrollOffset().top;
                // Skip re-scrolling when the user has already manually scrolled below this message,
                // or when we're already at this message and the chat still has more content below it.
                if (currentOffset > offset || (offset === this.lastScrollOffset && offset + scrollView.clientHeight() < scrollView.scrollHeight())) {
                    return;
                }

                this.lastScrollOffset = offset;
                scrollView.scrollTo({ top: offset });
            }
        }
    }

    private scrollToBottom() {
        const el = this.elementRef?.nativeElement.querySelector(".dx-chat .dx-scrollable");
        const scrollView = dxScrollView.getInstance(el) as dxScrollView;
        if (scrollView) {
            scrollView.scrollTo({ top: scrollView.scrollHeight() });
        }
    }
}
